<script setup lang="ts">
import { ChevronDownIcon, ChevronRightIcon, ReOrderDotsVerticalIcon } from '@blpde/nx-icons';
import {
  inject,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  useId,
  useTemplateRef,
  watch,
  type HTMLAttributes,
} from 'vue';
import {
  i18nInjectionKey,
  inputContainerInjectionKey,
  stickyContainerGroupInjectionKey,
  type StickyHandle,
} from './internal';
import { NxButton } from '../Button';

type InputContainerProps = {
  /** The text content of the section header. */
  labelText: string;
  /**
   * Optional 1-based section number. When provided, it is rendered in the header
   * and registers this section with an enclosing `NxStickyContainerGroup` so the
   * scrubber can navigate to it.
   */
  index?: number;
  /** The unique identifier of the element in the DOM. Defaults to a generated identifier */
  id?: string;
  /**
   * @deprecated Layout is now a reactive auto-fit grid; the column count is
   * derived from the available width, not this prop. Still accepted for
   * backwards compatibility, but ignored for layout.
   */
  columns?: 1 | 2;
  showMoreButton?: boolean;
  /** Whether additional items should be shown on setup. Defaults to `false` */
  initialShowMore?: boolean;
  /** Currently unused, see https://git.blp.de/BLP/formica/issues/5656 */
  showDragHandle?: boolean;
  collapsable?: boolean;
  /** Transformation of the label text. Defaults to `'uppercase'` */
  textTransform?: 'uppercase' | 'none';
};

const props = defineProps<InputContainerProps & /* @vue-ignore */ HTMLAttributes>();
const collapsed = defineModel<boolean>('collapsed', { default: false });

defineSlots<{
  /** Modern path: items flow directly into the reactive grid. */
  default(): unknown;
  preHeader(): unknown;
  postHeader(): unknown;
  /** @deprecated Compat: rendered into the same grid flow as the default slot. */
  firstCol(): unknown;
  /** @deprecated Compat: rendered into the same grid flow as the default slot. */
  secondCol(): unknown;
}>();

// If no element ID is given, an unique identifier is generated here and assigned to the id attribute of the input container
const elementId = props.id ?? useId();

// Assign the showMore property to a reactive object. The reactive object is available for all its descendants trough the input container injection
const showMore = ref(props.initialShowMore);

function toggle() {
  collapsed.value = !collapsed.value;
}
const bodyId = `${elementId}_body`;

provide(inputContainerInjectionKey, { showMore });
const i18nInjection = inject(i18nInjectionKey)!;

// Register with an enclosing NxStickyContainerGroup so the scrubber knows about this section.
// Purely navigational — no sticky-header positioning.
const sectionRef = useTemplateRef<HTMLElement>('section');
const stickyGroup = inject(stickyContainerGroupInjectionKey, null);
let stickyHandle: StickyHandle | null = null;

function clearStickyRegistration(): void {
  stickyHandle?.unregister();
  stickyHandle = null;
}

function syncStickyRegistration(): void {
  if (!stickyGroup || !sectionRef.value || props.index === undefined) {
    clearStickyRegistration();
    return;
  }
  if (stickyHandle) {
    stickyHandle.update(props.labelText, props.index);
    return;
  }
  stickyHandle = stickyGroup.register(sectionRef.value, props.labelText, props.index);
}

onMounted(syncStickyRegistration);

watch(() => [props.index, props.labelText] as const, syncStickyRegistration);

onBeforeUnmount(clearStickyRegistration);
</script>
<template>
  <section
    :id="elementId"
    ref="section"
    :data-columns="$props.columns"
    :aria-labelledby="`${elementId}_label`"
    class="input-container"
  >
    <div
      class="input-container-head"
      :class="{ collapsable: $props.collapsable }"
      @click="$props.collapsable && toggle()"
    >
      <div v-if="$props.index !== undefined" class="input-container-index">
        {{ $props.index }}
      </div>

      <button
        v-if="$props.collapsable"
        type="button"
        class="input-container-toggle"
        :aria-expanded="!collapsed"
        :aria-controls="bodyId"
        :style="`--transform: ${$props.textTransform ?? 'uppercase'}`"
        @click.stop="toggle"
      >
        <ChevronRightIcon v-if="collapsed" :filled="true" class="nxt-icon-sm" />
        <ChevronDownIcon v-else :filled="true" class="nxt-icon-sm" />
        <span :id="`${elementId}_label`">{{ $props.labelText }}</span>
      </button>
      <div
        v-else
        :id="`${elementId}_label`"
        class="input-container-text"
        :style="`--transform: ${$props.textTransform ?? 'uppercase'}`"
      >
        {{ $props.labelText }}
      </div>

      <div class="input-container-actions" @click.stop>
        <slot name="preHeader" />
        <NxButton
          v-if="$props.showMoreButton"
          variant="ghost"
          height="xs"
          width="narrow"
          @click="showMore = !showMore"
        >
          {{
            showMore
              ? i18nInjection.t('common.input_container.show_less')
              : i18nInjection.t('common.input_container.show_more')
          }}
        </NxButton>
        <div v-if="$props.showDragHandle">
          <!-- TODO: Functionality of this icon should be done in the future -->
          <ReOrderDotsVerticalIcon :filled="true" class="nxt-icon-sm" />
        </div>
        <slot name="postHeader" />
      </div>
    </div>

    <div v-show="!$props.collapsable || !collapsed" :id="bodyId" class="input-container-body">
      <!-- Reactive auto-fit grid: items flow into as many columns as fit (up to
           the cap), each at least --col-min wide, growing to 1fr. The default
           slot is the modern path; firstCol/secondCol stay accepted and flow
           into the same grid in order (compat bridge — `columns` is ignored). -->
      <slot />
      <slot name="firstCol" />
      <slot name="secondCol" />
    </div>
  </section>
</template>
<style scoped>
.input-container,
.input-container-head,
.input-container-body {
  width: 100%;
  min-width: 0;
}

.input-container-index {
  width: 1ch;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--nxt-space-2xs) var(--nxt-space-xs) var(--nxt-space-2xs) var(--nxt-space-md);
  font-family: var(--nxt-font);
  font-size: var(--nxt-font-size-md);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-section-label-fg);
}

.input-container-text {
  flex-grow: 1;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  font-family: var(--nxt-font);
  font-size: var(--nxt-font-size-md);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-section-label-fg);
  text-transform: var(--transform);
}

.input-container {
  max-width: 100%;
  container-type: inline-size;
  /* Surface-treatment tokens — remapped per data-nx-surface (I-160). No box
     border: card reads as a card via the shadow, hairline via the header line. */
  border: none;
  box-shadow: var(--nxd-surface-shadow);
  border-radius: var(--nxd-surface-radius);
  background-color: var(--nxd-surface-bg);
  overflow: hidden; /* clip the header band to the radius */
}

.input-container-head.collapsable {
  cursor: pointer;
}

.input-container-actions {
  display: contents;
}

.input-container-toggle {
  flex-grow: 1;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  margin: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: var(--nxt-font-size-md); /* match .input-container-text */
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-section-label-fg);
  text-align: left;
  text-transform: var(--transform);
  cursor: pointer;
}

.input-container-head {
  display: flex;
  align-items: center;
  gap: 0.125em; /* 2px */
  padding-inline: var(--nxt-space-2xs); /* breathing room from the card edge */
  /* Header band: filled in card, transparent in hairline; subtle bottom line in
     both. Header buttons are NxButton (variant link) — no internal style override. */
  background-color: var(--nxd-surface-head-fill);
  border-bottom: var(--nxd-surface-head-border);
}

.input-container-body {
  /* Inset the body within the card; horizontal padding aligns with the header. */
  padding: var(--nxt-space-md);

  /* Reactive auto-fit grid (capped "RAM" technique). Each track is at least
     --col-min wide and grows to 1fr so columns always fill the row. The inner
     max() pins the track to the --col-cap-column share, so auto-fit never
     produces more than --col-cap columns; below that width the grid drops to
     fewer columns intrinsically — no media/container query needed. The min()
     guards against horizontal overflow when the body is narrower than
     --col-min. */
  --col-min: 18em;
  --col-cap: 4;
  --col-gap: var(--nxt-space-md);
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(
      min(
        100%,
        max(var(--col-min), (100% - (var(--col-cap) - 1) * var(--col-gap)) / var(--col-cap))
      ),
      1fr
    )
  );
  gap: var(--col-gap) var(--col-gap); /* row gap tight, column gap = --col-gap */
  align-items: start; /* fields keep natural height, no row stretch */
}
</style>
